import React from "react";
import ReporteResizePdf from "./InformePdf/ReporteResizePdf";
import ReporteApagadosPdf from "./InformePdf/ReporteApagadosPdf";
import ReporteResumenEc2 from "./InformePdf/ReporteResumenEc2";
import PropTypes from "prop-types";
import LogoSiesa from "../../../assets/images/logo-siesa-nuevo.jpg";
import LogoJuntos from "../../../assets/images/juntos-mas-simple.png";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";

// Estilos del PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  bold: {
    fontWeight: "bold",
  },
  header: {
    marginBottom: 20,
    borderBottom: "1 solid #e0e0e0",
    paddingBottom: 10,
  },
  title: {
    fontSize: 16,
    marginBottom: 5,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 12,
    marginBottom: 5,
    textAlign: "center",
  },
  logo: {
    width: 100,
    height: "auto",
  },
  tittleContainer: {
    marginTop: 20,
    textAlign: "center",
  },
  date: {
    fontSize: 10,
    textAlign: "right",
    marginBottom: 10,
  },
  section: {
    marginBottom: 15,
    padding: 10,
    border: "1 solid #f0f0f0",
    borderRadius: 4,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#333",
  },
  chartContainer: {
    marginVertical: 10,
  },
  chartImage: {
    width: "100%",
    height: 200,
    objectFit: "contain",
  },
  table: {
    display: "table",
    width: "auto",
    marginBottom: 10,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
    padding: 5,
    fontSize: 9,
  },
  tableCol: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    fontSize: 8,
  },
});

const currentDate = new Date().toLocaleDateString("es-CO", {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

// Tabla: Discos EC2
const DiscosTablePDF = ({ data }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>Discos EC2</Text>
    <View style={styles.table}>
      <View style={styles.tableRow}>
        <Text style={styles.tableColHeader}>Disco</Text>
        <Text style={styles.tableColHeader}>Tamaño</Text>
        <Text style={styles.tableColHeader}>Uso</Text>
        <Text style={styles.tableColHeader}>% Libre</Text>
      </View>
      {data.map((row, idx) => (
        <View key={idx} style={styles.tableRow}>
          <Text style={styles.tableCol}>{row.disk || "-"}</Text>
          <Text style={styles.tableCol}>{row.total_size || "-"}</Text>
          <Text style={styles.tableCol}>{row.used_space || "-"}</Text>
          <Text style={styles.tableCol}>{row.free_percent ? `${row.free_percent}%` : "-"}</Text>
        </View>
      ))}
    </View>
  </View>
);

const TableInfoEc2 = ({ data, disponibilidad }) => {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Información Ec2 Cliente</Text>
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <Text style={styles.tableColHeader}>Cliente</Text>
          <Text style={styles.tableColHeader}>Tipo Instancia</Text>
          <Text style={styles.tableColHeader}>Cores Cpu</Text>
          <Text style={styles.tableColHeader}>Memoria Ram</Text>
          {/* <Text style={styles.tableColHeader}>% Disponibilidad</Text> */}
        </View>
        {data?.informacionEc2?.rows?.map((row, idx) => (
          <View key={idx} style={styles.tableRow}>
            <Text style={styles.tableCol}>{row.cliente || "-"}</Text>
            <Text style={styles.tableCol}>{row.instance_type || "-"}</Text>
            <Text style={styles.tableCol}>{row.default_vcpus || "-"}</Text>
            <Text style={styles.tableCol}>{row.memory_info / 1024 || "-"}</Text>
            {/* <Text style={styles.tableCol}>{disponibilidad.toFixed(2)}</Text> */}
          </View>
        ))}
      </View>
    </View>
  );
};

// Tabla: Métricas EC2
const MetricasTablePDF = ({ data }) => {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Métricas EC2 (CPU/RAM)</Text>
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <Text style={styles.tableColHeader}>Fecha</Text>
          <Text style={styles.tableColHeader}>CPU (%)</Text>
          <Text style={styles.tableColHeader}>RAM (%)</Text>
          <Text style={styles.tableColHeader}>Estado</Text>
        </View>
        {data?.infoEc2?.rows?.map((row, idx) => (
          <View key={idx} style={styles.tableRow}>
            <Text style={styles.tableCol}>
              {new Date(parseInt(row.t) * 1000).toLocaleString("es-CO")}
            </Text>
            <Text style={styles.tableCol}>{parseFloat(row.c).toFixed(2)}</Text>
            <Text style={styles.tableCol}>{parseFloat(row.r).toFixed(2)}</Text>
            <Text style={styles.tableCol}>
              {parseFloat(row.c) > 90 || parseFloat(row.r) > 90 ? "Alerta" : "Normal"}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const MetricasMaximoPDF = ({ data }) => {
  const result = Object.values(
    data?.infoEc2?.rows?.reduce((acc, item) => {
      const fechaHora = new Date(parseInt(item.t) * 1000).toLocaleString("es-CO");
      const soloFecha = new Date(parseInt(item.t) * 1000).toLocaleDateString("es-CO");

      const cpu = Number(item.c);
      const ram = Number(item.r);

      if (!acc[soloFecha]) {
        acc[soloFecha] = {
          fecha: soloFecha,
          maxCpu: { cpu, ram, fechaHora },
          maxRam: { ram, cpu, fechaHora },
        };
      } else {
        if (cpu > acc[soloFecha].maxCpu.cpu) {
          acc[soloFecha].maxCpu = { cpu, ram, fechaHora };
        }

        if (ram > acc[soloFecha].maxRam.ram) {
          acc[soloFecha].maxRam = { ram, cpu, fechaHora };
        }
      }

      return acc;
    }, {})
  );

  const finalData = Object.values(result);

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Picos Máximos EC2 (CPU / RAM)</Text>

      <View style={styles.table}>
        <View style={styles.tableRow}>
          <Text style={styles.tableColHeader}>Fecha</Text>
          <Text style={styles.tableColHeader}>Tipo</Text>
          <Text style={styles.tableColHeader}>CPU (%)</Text>
          <Text style={styles.tableColHeader}>RAM (%)</Text>
          <Text style={styles.tableColHeader}>Hora</Text>
        </View>

        {finalData.map((row, idx) => (
          <React.Fragment key={idx}>
            <View style={styles.tableRow}>
              <Text style={styles.tableCol}>{row.fecha}</Text>
              <Text style={styles.tableCol}>Máx CPU</Text>
              <Text style={styles.tableCol}>{row.maxCpu.cpu.toFixed(2)}</Text>
              <Text style={styles.tableCol}>{row.maxCpu.ram.toFixed(2)}</Text>
              <Text style={styles.tableCol}>{row.maxCpu.fechaHora}</Text>
            </View>

            <View style={styles.tableRow}>
              <Text style={styles.tableCol}>{row.fecha}</Text>
              <Text style={styles.tableCol}>Máx RAM</Text>
              <Text style={styles.tableCol}>{row.maxRam.cpu.toFixed(2)}</Text>
              <Text style={styles.tableCol}>{row.maxRam.ram.toFixed(2)}</Text>
              <Text style={styles.tableCol}>{row.maxRam.fechaHora}</Text>
            </View>
          </React.Fragment>
        ))}
      </View>
    </View>
  );
};

// Documento PDF Principal
export const MyPdfDocument = ({
  activeDirectoryData,
  chartImageDataUrl,
  discosData,
  ec2Data,
  disponibilidad,
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Image src={LogoSiesa} style={styles.logo} />
        <Image src={LogoJuntos} style={styles.logo} />
      </View>
      <View style={styles.tittleContainer}>
        <Text style={styles.title}>Informe de disponibilidad Siesa Cloud Services</Text>
        <Text style={styles.subtitle}>Reporte de métricas de instancia EC2</Text>
        <Text style={styles.date}>
          Generado el: {currentDate} a través de Pos1 (Arquitectura Nube)
        </Text>
        <Text>
          Descripción: Informe controles SOX para su infraestructura custodiada por Siesa.
        </Text>
      </View>
      {/* Gráfico CPU/RAM */}
      {chartImageDataUrl && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Métricas de CPU y RAM</Text>
          <View style={styles.chartContainer}>
            <Image src={chartImageDataUrl} style={styles.chartImage} cache={false} />
          </View>
        </View>
      )}
      {/* Información EC2 */}
      {ec2Data?.informacionEc2?.rows?.length > 0 && (
        <TableInfoEc2 data={ec2Data} disponibilidad={disponibilidad} />
      )}

      {/* Tabla Discos EC2 */}
      {discosData && discosData.length > 0 && <DiscosTablePDF data={discosData} />}

      {/* Tabla Métricas EC2 */}
      {ec2Data?.infoEc2?.rows?.length > 0 && <MetricasMaximoPDF data={ec2Data} />}

      <ReporteResumenEc2 data={ec2Data} />

      <Text>
        <Text style={styles.bold}>Descripción:</Text> A nivel de arquitectura se realiza un proceso
        automático denominado Resize o Resizing, el cual permite controlar y ajustar la capacidad de
        recursos del servidor de aplicación conforme la carga operativa que se requiera en
        determinado horario, permitiendo de esta manera que la infraestructura en nube sea auto
        escalable, es decir, que los recursos del servidor (CPU y memoria RAM), puedan aumentar o
        disminuir de manera automática según sus necesidades y operación. De esta manera, para dicho
        proceso de Resize se tiene implementado en los siguientes horarios: {"\n"}
      </Text>

      <View style={{ marginVertical: 8 }} />
      <ReporteResizePdf data={ec2Data.informacionResize} />

      <ReporteApagadosPdf data={ec2Data.informacionApagados} />

      <View style={{ marginVertical: 4 }} />
      <Text>
        Cabe resaltar que este proceso de Resize únicamente aplica para el servidor de aplicación
        Siesa Enterprise y que, durante el proceso, se genera una interrupción temporal del servicio
        entre 5 a 8 minutos aproximadamente debido al reinicio del servidor durante el ajuste de los
        recursos, por ende, estos tiempos se ven reflejados en el historial de inactividad, sin
        embargo, no significan una indisponibilidad real que afecte directamente la operación del
        cliente puesto que el proceso se realiza en un horario no hábil para su operación. Teniendo
        en cuenta lo anteriormente descrito, el porcentaje de disponibilidad Rango fecha fue del{" "}
        <Text style={styles.bold}>{disponibilidad.toFixed(2)}%</Text>
      </Text>
    </Page>
  </Document>
);

// Prop Types
DiscosTablePDF.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      disk: PropTypes.string,
      total_size: PropTypes.string,
      used_space: PropTypes.string,
      free_percent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
};

TableInfoEc2.propTypes = {
  data: PropTypes.shape({
    informacionEc2: PropTypes.shape({
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          cliente: PropTypes.string,
          instance_type: PropTypes.string,
          default_vcpus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          memory_info: PropTypes.string,
        })
      ),
    }),
  }).isRequired,
  disponibilidad: PropTypes.string,
};

MetricasTablePDF.propTypes = {
  data: PropTypes.shape({
    infoEc2: PropTypes.shape({
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          t: PropTypes.string,
          c: PropTypes.string,
          r: PropTypes.string,
        })
      ),
    }),
  }).isRequired,
};

MetricasMaximoPDF.propTypes = {
  data: PropTypes.shape({
    infoEc2: PropTypes.shape({
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          c: PropTypes.string,
          r: PropTypes.string,
          t: PropTypes.string,
        })
      ),
    }),
  }).isRequired,
};

MyPdfDocument.propTypes = {
  activeDirectoryData: PropTypes.array,
  chartImageDataUrl: PropTypes.string,
  discosData: PropTypes.array,
  ec2Data: PropTypes.object,
  dataEc2: PropTypes.object,
  disponibilidad: PropTypes.string,
};

export default MyPdfDocument;
